'use client';

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { type NodeProps } from '@xyflow/react';
import { NodeShell } from '../BaseNode';
import { usePipelineStore } from '@/lib/pipeline/store';
import { ParamNameField } from '../ParamNameField';
import { referenceCandidates, activeRefToken } from '@/lib/pipeline/refs';

type Candidate = { id: string; ref: string; label: string; preview: string };

export default function PromptNode({ id, data }: NodeProps) {
  const update = usePipelineStore((s) => s.updateNodeData);
  const nodes = usePipelineStore((s) => s.nodes);
  const edges = usePipelineStore((s) => s.edges);
  const rt = usePipelineStore((s) => s.runtime[id]);
  const d = data as { text?: string; param?: string | null };
  const resolved = (rt?.output as { text?: string } | undefined)?.text;

  const taRef = useRef<HTMLTextAreaElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // inline {-autocomplete state
  const [ac, setAc] = useState<{ start: number; items: Candidate[]; active: number } | null>(null);

  // Local mirror of the text: the textarea is driven by local state (synchronous with typing),
  // and the store is updated as a side-effect — avoids caret jumps from store re-renders.
  const [text, setText] = useState(d.text ?? '');
  useEffect(() => {
    if (document.activeElement !== taRef.current) setText(d.text ?? '');
  }, [d.text]);
  const commit = (v: string) => {
    setText(v);
    update(id, { text: v });
  };

  const allCandidates = (): Candidate[] => {
    const refs = referenceCandidates(nodes, id);
    const hasInput = edges.some((e) => e.target === id && e.targetHandle === 'input');
    return hasInput
      ? [{ id: 'input', ref: 'input', label: 'wired input', preview: '' }, ...refs]
      : refs;
  };

  const refreshAc = () => {
    const ta = taRef.current;
    if (!ta) return setAc(null);
    const tok = activeRefToken(ta.value, ta.selectionStart ?? 0);
    if (!tok) return setAc(null);
    const q = tok.query.trim().toLowerCase();
    const items = allCandidates().filter(
      (c) =>
        !q ||
        c.ref.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q) ||
        c.preview.toLowerCase().includes(q),
    );
    if (!items.length) return setAc(null);
    setAc({ start: tok.start, items, active: 0 });
  };

  const insertAt = (start: number, ref: string) => {
    const ta = taRef.current;
    const cur = text;
    const caret = ta?.selectionStart ?? cur.length;
    const tag = `{${ref}}`;
    commit(cur.slice(0, start) + tag + cur.slice(caret));
    setAc(null);
    setPickerOpen(false);
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      const p = start + tag.length;
      ta.setSelectionRange(p, p);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!ac) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAc((a) => (a ? { ...a, active: (a.active + 1) % a.items.length } : a));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAc((a) => (a ? { ...a, active: (a.active - 1 + a.items.length) % a.items.length } : a));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertAt(ac.start, ac.items[ac.active].ref);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAc(null);
    }
  };

  const dropdownCandidates = allCandidates();

  return (
    <NodeShell id={id} type="promptInput">
      <div className="relative">
        <textarea
          ref={taRef}
          className="input nodrag nowheel w-full text-sm min-h-[88px] resize-none"
          placeholder="Describe your image… type { to reference a node, param, or input"
          value={text}
          onChange={(e) => {
            commit(e.target.value);
            refreshAc();
          }}
          onKeyUp={refreshAc}
          onClick={refreshAc}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setAc(null), 120)}
        />
        {ac && (
          <div className="absolute z-30 left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-[var(--bg-elevated)] border border-[var(--accent)] rounded-lg shadow-soft nodrag nowheel">
            {ac.items.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertAt(ac.start, c.ref);
                }}
                className={`w-full text-left px-2 py-1.5 ${
                  i === ac.active ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--bg-hover)]'
                }`}
              >
                <span className="text-[11px] font-medium">
                  {'{'}
                  {c.ref}
                  {'}'} · {c.label}
                </span>
                {c.preview && (
                  <span className="block text-[10px] text-[var(--text-muted)] truncate">
                    {c.preview}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative flex justify-end">
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className="nodrag text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-subtle"
          title="Insert a reference to another node's output (or just type { )"
        >
          {'{ }'} insert reference ▾
        </button>
        {pickerOpen && (
          <div className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg shadow-soft nodrag nowheel">
            {dropdownCandidates.length === 0 ? (
              <div className="px-2 py-2 text-[10px] text-[var(--text-muted)]">
                No referenceable nodes (add a Prompt, Caption, or Text Join node)
              </div>
            ) : (
              dropdownCandidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() =>
                    insertAt(taRef.current?.selectionStart ?? taRef.current?.value.length ?? 0, c.ref)
                  }
                  className="w-full text-left px-2 py-1.5 hover:bg-[var(--bg-hover)] transition-subtle"
                >
                  <span className="text-[11px] font-medium">
                    {'{'}
                    {c.ref}
                    {'}'} · {c.label}
                  </span>
                  {c.preview && (
                    <span className="block text-[10px] text-[var(--text-muted)] truncate">
                      {c.preview}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {resolved != null && resolved !== text && (
        <div className="text-[10px] text-[var(--success)] mt-1 line-clamp-3" title={resolved}>
          → {resolved}
        </div>
      )}
      <ParamNameField id={id} value={d.param} />
    </NodeShell>
  );
}
